import { Box } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { IComposableDashboardContext } from 'wisestep-api';
import { WsTypography } from 'wisestep-base';

import { StandardHeightsByRows } from '../../..';
import { useComposableDashboardsContext } from '../../../../../features/analyticsPage/hooks/useComposableDashboardsContext';
import { Widget } from '../../../../../features/composableAnalytics/DerivedCharts/helper';
import { stageStatusPipelineType } from '../../../../../features/crmSettings/customizeStageStatus/helper';
import { useStageStatusContext } from '../../../../../features/submissions/context/StageStatusContext';
import { useComposableDashboardsFiltersContext } from '../../../../analyticsPage/hooks/useComposableDashboardsFiltersContext';
import { useAnalyticsDashboardEditActionStore } from '../../../../analyticsPage/store/useAnalyticsDashboardEditActionStore';
import { CustomNoRowsOverlay } from '../../../helper/showCustomizeNoDataTextDataGrid';
import usePostgreWidgetData from '../../../hooks/usePostgreWidgetData';
import {
  columnVisibilityModel,
  CustomToolbar,
  defaultWidgetData,
  renderCellFor,
} from '../../helper';
import { IPostgreSubmissionDTO, IPostgreSubmissionWidgetProp } from '../types';
import { getColumns, getDynamicColumns, getTableData } from '../utils/submissionHelper';

const SubmissionsTablePostgre = ({
  query,
  title,
  widgetName,
  widgetType = '',
}: IPostgreSubmissionWidgetProp) => {
  const isViewmode = useAnalyticsDashboardEditActionStore((state) => state.isViewmode);
  const { widgetData } = usePostgreWidgetData<IPostgreSubmissionDTO>(query, defaultWidgetData);
  const { restructuredFilters } = useComposableDashboardsFiltersContext();
  const { stageStatusPipeline } = useStageStatusContext();
  const tablesWithDynamicRows = [Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE_MEDIAN];
  const { updateTableWidget, selectedDashboard } =
    useComposableDashboardsContext() as Required<IComposableDashboardContext>;

  const rowNum = [
    Widget.TABLE_JOB_TITLE,
    Widget.TABLE_RECRUITERWISE,
    Widget.TABLE_BY_DOWNLOAD_SOURCE,
    Widget.TABLE_BY_JOBTYPE,
    Widget.TABLE_BY_SOURCE,
  ].includes(widgetName)
    ? 3
    : 5;

  const sortedField = [
    Widget.TABLE_CLIENTWISE,
    Widget.TABLE_BY_AM,
    Widget.TABLE_USERS_WITH_TEAMS,
  ].includes(widgetName)
    ? 'totalCount'
    : widgetName.includes(Widget.TABLE_JOINING_DATE)
    ? 'joiningDate'
    : 'count';

  const tableData = getTableData(widgetName, widgetData, query.frequency as string) ?? [];

  let columns: Array<GridColDef> = [];
  if (tablesWithDynamicRows.includes(widgetName)) {
    switch (widgetName) {
      case Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE_MEDIAN: {
        const stages: stageStatusPipelineType[] = JSON.parse(stageStatusPipeline ?? '[]');
        const orderedStages = stages.sort(({ stageOrder: a }, { stageOrder: b }) => a - b);

        columns = getDynamicColumns(widgetName, [
          {
            label: 'Candidate Name',
            accessor: 'candidateName',
            renderCell: (params) => renderCellFor('CANDIDATE', params),
            flex: 1.5,
          },
          ...orderedStages.map(({ stage }) => ({ label: stage, accessor: stage, flex: 1 })),
        ]);
        break;
      }
      default:
        columns = [];
    }
  } else {
    columns = getColumns(widgetName, widgetData, tableData, query, restructuredFilters);
  }

  return (
    <Box>
      <WsTypography bold={true}>{title}</WsTypography>
      <Box mt={1.5} sx={{ height: StandardHeightsByRows[rowNum] }}>
        <DataGrid
          columns={columns}
          rows={tableData}
          components={{
            Toolbar: () => <CustomToolbar isViewMode={isViewmode} />,
            NoRowsOverlay: CustomNoRowsOverlay,
          }}
          disableColumnSelector={!isViewmode}
          pageSize={rowNum}
          rowsPerPageOptions={[rowNum]}
          sx={{ overflowY: 'hidden' }}
          onColumnVisibilityModelChange={(newModel) => {
            const hiddenColumns = Object.keys(newModel).filter((column) => !newModel[column]);
            updateTableWidget(widgetType, { hiddenColumns });
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: sortedField, sort: 'desc' }],
            },
            columns: {
              columnVisibilityModel: columnVisibilityModel(selectedDashboard, widgetType),
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default SubmissionsTablePostgre;
